import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { verificationAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  cleared: { color: '#059669', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', label: 'Cleared' },
  pending: { color: '#0076A3', bg: 'rgba(30,104,255,0.08)', border: 'rgba(30,104,255,0.2)', label: 'Pending' },
  action_required: { color: '#D97706', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', label: 'Action Required' },
  blocked: { color: '#DC2626', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)', label: 'Blocked' },
  expired: { color: '#DC2626', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)', label: 'Expired' },
};

export default function VerificationDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const res = await verificationAPI.getDashboard();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load verification data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleReminder = async (id) => {
    try {
      await verificationAPI.sendReminder(id);
      toast.success('Reminder sent');
      loadData();
    } catch (err) { toast.error('Failed to send reminder'); }
  };

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:'#64748B' }}>Loading verification dashboard...</div>;

  const summary = data?.summary || {};
  const requests = data?.requests || [];
  const filtered = filter === 'all' ? requests : requests.filter(r => r.clearance_status === filter);

  return (
    <div data-testid="verification-dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Background Verification</h1>
          <p className="page-subtitle">Track candidate clearance and compliance status</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="kpi-grid" style={{ marginBottom: '1.5rem' }}>
        {[
          { label: 'Cleared', value: summary.cleared || 0, color: '#059669' },
          { label: 'Pending', value: summary.pending || 0, color: '#0076A3' },
          { label: 'Action Required', value: summary.action_required || 0, color: '#D97706' },
          { label: 'Blocked', value: summary.blocked || 0, color: '#DC2626' },
          { label: 'Total', value: summary.total || 0, color: '#002A38' },
        ].map(({ label, value, color }) => (
          <div key={label} className="kpi-tile" onClick={() => setFilter(label === 'Total' ? 'all' : label.toLowerCase().replace(' ', '_'))} style={{ cursor: 'pointer' }}>
            <div className="kpi-label">{label}</div>
            <div className="kpi-value" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {['all', 'cleared', 'pending', 'action_required', 'blocked', 'expired'].map(f => {
          const cfg = STATUS_CONFIG[f] || { color: '#002A38', bg: 'rgba(0,0,0,0.04)', border: '#ECE7DE' };
          return (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '0.375rem 0.875rem', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', border: 'none',
              background: filter === f ? (f === 'all' ? '#0076A3' : cfg.bg) : '#F1EAE1',
              color: filter === f ? (f === 'all' ? '#fff' : cfg.color) : '#64748B',
              outline: filter === f ? `2px solid ${f === 'all' ? '#0076A3' : cfg.border}` : 'none',
              outlineOffset: 1,
            }}>
              {f === 'all' ? 'All' : (STATUS_CONFIG[f]?.label || f)}
            </button>
          );
        })}
      </div>

      {/* Request list */}
      {filtered.length === 0 ? (
        <div className="empty-state" style={{ paddingTop: '3rem' }}>
          <p>No verification requests {filter !== 'all' ? `with status "${filter}"` : 'yet'}. Create one from a Job's detail page.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(req => {
            const cfg = STATUS_CONFIG[req.clearance_status] || STATUS_CONFIG.pending;
            return (
              <Card key={req._id} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #0076A3, #00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#fff' }}>
                      {req.candidate_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38' }}>{req.candidate_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{req.candidate_email} &middot; {req.job_title}</div>
                    </div>
                  </div>
                  <Badge style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 999, fontSize: '0.6875rem' }}>
                    {cfg.label}
                  </Badge>
                </div>

                <div style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: '0.75rem' }}>
                  {req.clearance_message}
                </div>

                {(req.clearance_reasons || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.75rem' }}>
                    {req.clearance_reasons.map((r, i) => (
                      <span key={`reason-${req._id}-${i}`} style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: '#FEF3C7', border: '1px solid #FDE68A', fontSize: '0.6875rem', color: '#92400E' }}>{r}</span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                    {req.documents_submitted || 0} docs submitted &middot; {req.documents_approved || 0} approved &middot; {req.total_mandatory || 0} required
                    {req.reminder_count > 0 && ` &middot; ${req.reminder_count} reminder(s) sent`}
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem' }}>
                    <Button variant="outline" onClick={() => handleReminder(req._id)} style={{ borderRadius: 8, fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}>Send Reminder</Button>
                    <Button onClick={() => navigate(`/verification/${req._id}`)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}>View Details</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
